export const NARROW_MAX_WIDTH_REM = 46;

export function rootFontSizePx() {
  if (typeof document === 'undefined') {
    return 16;
  }
  const parsed = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
}

export function isNarrowWidth(width: number, fontSize = rootFontSizePx()) {
  if (!(width > 0)) {
    return false;
  }
  return width < NARROW_MAX_WIDTH_REM * fontSize;
}

type NarrowColumn = { id: string; isRowHeader?: boolean };

type NarrowPriority = ReadonlyArray<string | readonly string[]>;

function priorityLines(priority: NarrowPriority) {
  return priority.map((entry) => (typeof entry === 'string' ? [entry] : [...entry]));
}

function mainColumnIdForNarrow<T extends NarrowColumn>(columns: readonly T[], mainColumnId: string | undefined) {
  const ids = new Set(columns.map((column) => column.id));
  if (mainColumnId !== undefined && ids.has(mainColumnId)) {
    return mainColumnId;
  }
  return columns.find((column) => column.isRowHeader)?.id ?? columns[0]?.id;
}

export function foldedLinesForNarrow<T extends NarrowColumn>(
  columns: readonly T[],
  options: { mainColumnId?: string; priority: NarrowPriority },
) {
  const ids = new Set(columns.map((column) => column.id));
  const mainId = mainColumnIdForNarrow(columns, options.mainColumnId);
  const lines: string[][] = [];
  const seen = new Set<string>();

  for (const group of priorityLines(options.priority)) {
    const line = group.filter((id) => id !== mainId && ids.has(id) && !seen.has(id));
    for (const id of line) {
      seen.add(id);
    }
    if (line.length > 0) {
      lines.push(line);
    }
  }

  for (const column of columns) {
    if (column.id !== mainId && !seen.has(column.id)) {
      lines.push([column.id]);
      seen.add(column.id);
    }
  }

  return lines;
}

export function foldedIdsForNarrow<T extends NarrowColumn>(
  columns: readonly T[],
  options: { mainColumnId?: string; priority: NarrowPriority },
) {
  return foldedLinesForNarrow(columns, options).flat();
}
